import { useCallback, useEffect, useRef, useState } from "react";

type SetValue<T> = (value: T | ((prev: T) => T)) => void;

/**
 * Persistent state synced to localStorage with safe fallbacks for:
 * - SSR (typeof window === "undefined")
 * - Corrupted JSON (returns defaultValue)
 * - QuotaExceededError on write (logs warning, keeps in-memory state)
 *
 * Note: not cross-tab reactive. Two tabs do not see each other's writes.
 * If we ever need that, add a `storage` event listener, deferred for now.
 */
export function useLocalStorage<T>(
  key: string,
  defaultValue: T
): [T, SetValue<T>] {
  const [value, setValueState] = useState<T>(() => {
    if (typeof window === "undefined") return defaultValue;
    try {
      const raw = window.localStorage.getItem(key);
      if (raw === null) return defaultValue;
      return JSON.parse(raw) as T;
    } catch {
      return defaultValue;
    }
  });

  // Mirror current value in a ref so the setter sees fresh state when the
  // caller passes a functional updater inside a useCallback with stale deps.
  const valueRef = useRef(value);
  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  const setValue = useCallback<SetValue<T>>(
    next => {
      const resolved =
        typeof next === "function"
          ? (next as (prev: T) => T)(valueRef.current)
          : next;
      setValueState(resolved);
      if (typeof window === "undefined") return;
      try {
        window.localStorage.setItem(key, JSON.stringify(resolved));
      } catch (err) {
        if (import.meta.env.DEV) {
          // eslint-disable-next-line no-console
          console.warn(`[useLocalStorage] write failed for "${key}"`, err);
        }
      }
    },
    [key]
  );

  return [value, setValue];
}
